import { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CURRENCY_LIST } from '../data/currencies.js';

export default function CurrencySelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const filtered = CURRENCY_LIST.filter(c =>
    c.code.toLowerCase().includes(search.toLowerCase()) ||
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const selected = CURRENCY_LIST.find(c => c.code === value);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = useCallback((code) => {
    onChange(code);
    setOpen(false);
    setSearch('');
  }, [onChange]);

  return (
    <div className="currency-selector" ref={containerRef}>
      <button
        className="currency-selector__trigger"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        type="button"
      >
        <span className="currency-selector__flag">{selected?.flag}</span>
        <span className="currency-selector__code">{value}</span>
        <span className="currency-selector__symbol">{selected?.symbol}</span>
        <svg className={`currency-selector__chevron${open ? ' open' : ''}`} width="12" height="12" viewBox="0 0 12 12">
          <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="currency-selector__dropdown" role="listbox" aria-label="Select currency">
          <div className="currency-selector__search-wrap">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              ref={searchRef}
              className="currency-selector__search"
              type="text"
              placeholder="Search currency..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="Search currencies"
            />
          </div>
          <div className="currency-selector__list">
            {filtered.length === 0 && (
              <div className="currency-selector__empty">No currencies found</div>
            )}
            {filtered.map(c => (
              <button
                key={c.code}
                className={`currency-selector__option${c.code === value ? ' selected' : ''}`}
                onClick={() => handleSelect(c.code)}
                role="option"
                aria-selected={c.code === value}
                type="button"
              >
                <span className="currency-selector__option-flag">{c.flag}</span>
                <span className="currency-selector__option-code">{c.code}</span>
                <span className="currency-selector__option-name">{c.name}</span>
                <span className="currency-selector__option-symbol">{c.symbol}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

CurrencySelector.propTypes = {
  value:    PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
}